'use client'
import { cn } from '@/util/cn'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { Icon } from 'ui'
import IconButton from 'ui/src/components/controls/icon-button'

type Option = {
    label: ReactNode
    icon?: string
    /**
     * Include hrefs in the toggle
     * This is useful for SEO
     * Currently this does not render correctly for users, but it works for bots
     */
    href?: string
    gradientClassName?: string
}

type ToggleProps = {
    className?: string
    options: Option[]
    index: number
    onClick?: (index: number) => void
    isMobile?: boolean
    // use this if you need to navigate during the transition
    // if you try to update the url during an animation, it will cancel the animation
    onTransitionEnd?: (index: number) => void
}
const Toggle = ({ className, options, onClick, index, onTransitionEnd, isMobile }: ToggleProps) => {
    const [_index, setIndex] = useState(index)
    const isTransitioning = useRef(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const pathname = usePathname()
    const isSearchPage = pathname?.startsWith('/search')
    const isEditGifsPage = pathname?.startsWith('/edit/gifs')

    useEffect(() => {
        if (!isTransitioning.current && index !== _index) {
            setIndex(index)
        }
    }, [_index, index, setIndex])

    useEffect(() => {
        if (isMobile && containerRef.current) {
            const container = containerRef.current
            const optionWidth = 96
            const scrollPosition = _index * optionWidth - container.clientWidth / 2 + 44

            container.scrollTo({
                left: Math.max(0, scrollPosition),
                behavior: 'smooth',
            })
        }
    }, [_index, isMobile])

    const buttonWidth = isMobile
        ? isSearchPage
            ? `${100 / options.length}%`
            : '88px'
        : `${100 * (1 / options.length)}%`
    const left = isMobile
        ? isSearchPage
            ? `${100 * (_index / options.length)}%`
            : `${_index * 96}px`
        : `${100 * (_index / options.length)}%`

    const gradient = ['bg-gradient-primary', 'bg-gradient-secondary', 'bg-gradient-tertiary']

    const toggleContent = (
        <div
            key={`toggle-${isMobile ? 'mobile' : 'desktop'}-${isSearchPage ? 'search' : 'normal'}`}
            className={cn(
                'relative flex flex-shrink-0 justify-between overflow-hidden rounded-3xl bg-gradient-grey font-bold text-giphyWhite',
                isMobile
                    ? 'w-max min-w-full'
                    : ['flex w-full justify-between overflow-hidden', !isEditGifsPage && 'md:w-[400px]'],
                !isMobile && className
            )}
        >
            <div
                className={cn(
                    'absolute left-0 z-10 h-full transition-all duration-250',
                    isMobile
                        ? 'rounded-3xl bg-gradient-tertiary'
                        : (options[index].gradientClassName || gradient[_index % options.length]) + ' rounded-[inherit]'
                )}
                onTransitionEnd={() => {
                    onTransitionEnd?.(_index)
                    isTransitioning.current = false
                }}
                style={{
                    width: buttonWidth,
                    left,
                    transform: isMobile ? 'translateZ(0)' : undefined, // Force hardware acceleration on mobile
                }}
            />
            <div
                className={cn(
                    'flex',
                    isMobile ? (isSearchPage ? 'w-full justify-between' : 'gap-2') : 'w-full justify-between'
                )}
            >
                {options.map((option, mapIndex) => {
                    const handleClick = () => {
                        if (_index !== mapIndex) {
                            isTransitioning.current = true
                            setIndex(mapIndex)
                            onClick?.(mapIndex)
                        }
                    }

                    const buttonContent = (
                        <>
                            {option.icon && <IconButton inverted icon={option.icon as Icon} />}
                            {option.label}
                        </>
                    )

                    const buttonStyle = isMobile
                        ? isSearchPage
                            ? {
                                  width: buttonWidth,
                                  minWidth: buttonWidth,
                                  height: '45px',
                                  flexShrink: 0,
                                  whiteSpace: 'nowrap' as const,
                              }
                            : {
                                  width: '88px',
                                  minWidth: '88px',
                                  height: '45px',
                                  flexShrink: 0,
                                  scrollSnapAlign: 'center',
                                  whiteSpace: 'nowrap' as const,
                              }
                        : {
                              width: buttonWidth,
                          }

                    return option.href ? (
                        <Link
                            replace
                            onClick={handleClick}
                            className={cn(
                                'relative z-20 flex cursor-pointer items-center justify-center gap-1 rounded-3xl text-center text-inherit',
                                isMobile ? 'px-3 py-2' : 'p-2'
                            )}
                            key={mapIndex}
                            style={buttonStyle}
                            href={option.href}
                        >
                            {buttonContent}
                        </Link>
                    ) : (
                        <button
                            onClick={handleClick}
                            className={cn(
                                'relative z-20 flex cursor-pointer items-center justify-center gap-1 rounded-3xl text-inherit',
                                isMobile ? 'px-3 py-2' : 'p-2'
                            )}
                            key={mapIndex}
                            type="button"
                            style={buttonStyle}
                        >
                            {buttonContent}
                        </button>
                    )
                })}
            </div>
        </div>
    )

    if (!isMobile) return toggleContent

    return (
        <div
            ref={containerRef}
            className={cn(
                'scrollbar-hide overflow-x-auto', // eslint-disable-line better-tailwindcss/no-unregistered-classes
                className
            )}
            style={{
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
            }}
        >
            {toggleContent}
        </div>
    )
}
export default Toggle
